/**
 * RevenueCatIdentityProvider — binds RevenueCat's appUserId to Supabase
 * `user.id` BEFORE any paywall opens. No temp/anonymous IDs, no purchase
 * linking, no auto-restore.
 *
 * Despia iOS path: fires `revenuecat://login?external_id=<user.id>` once per
 *   (user.id) via the Despia bridge. The web/PWA path uses Stripe, not the
 *   RC JS SDK, so identity is a no-op there and status resolves to 'ready'
 *   immediately so paywall callers don't block.
 *
 * Soft-paywall rewrite, Step 3. See .lovable/plan.md.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { isDespiaIOS } from '@/hooks/use-despia';

export type RevenueCatIdentityStatus = 'idle' | 'identifying' | 'ready' | 'error';

interface RevenueCatIdentityContextValue {
  status: RevenueCatIdentityStatus;
  /** RC appUserId currently bound (always === supabase user.id when ready). */
  appUserId: string | null;
  /**
   * Await this before opening any paywall. Resolves to true when RC is bound
   * to the current user.id (or when binding is a no-op on this platform);
   * false if no signed-in user or the bridge errored.
   */
  ensureIdentity: () => Promise<boolean>;
}

const Ctx = createContext<RevenueCatIdentityContextValue | null>(null);

async function despiaLogin(userId: string): Promise<void> {
  const { default: despia } = await import('despia-native');
  const url = `revenuecat://login?external_id=${encodeURIComponent(userId)}`;
  const result = despia(url);
  // Best-effort: log a rejection but don't throw — the native bridge is
  // fire-and-forget on most iOS versions.
  if (result && typeof (result as any).catch === 'function') {
    (result as any).catch((err: unknown) => {
      console.warn('[RC-Identity] despia login rejected', err);
    });
  }
}

export function RevenueCatIdentityProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<RevenueCatIdentityStatus>('idle');
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const boundIdRef = useRef<string | null>(null);
  const pendingRef = useRef<Promise<boolean> | null>(null);

  const bind = useCallback(async (userId: string): Promise<boolean> => {
    if (boundIdRef.current === userId) {
      setStatus('ready');
      setAppUserId(userId);
      return true;
    }
    setStatus('identifying');
    try {
      if (isDespiaIOS()) {
        await despiaLogin(userId);
      }
      // Web: RC JS SDK not in use here (Stripe handles web). Identity is a
      // platform no-op — treat as ready so paywall flows aren't blocked.
      boundIdRef.current = userId;
      setAppUserId(userId);
      setStatus('ready');
      console.log('[RC-Identity] bound', { userId, native: isDespiaIOS() });
      return true;
    } catch (err) {
      console.error('[RC-Identity] bind failed', err);
      setStatus('error');
      return false;
    }
  }, []);

  // Auto-bind whenever the Supabase user changes.
  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) {
      boundIdRef.current = null;
      setAppUserId(null);
      setStatus('idle');
      return;
    }
    if (boundIdRef.current === user.id) return;
    void bind(user.id);
  }, [authLoading, user?.id, bind]);

  const ensureIdentity = useCallback(async (): Promise<boolean> => {
    if (!user?.id) return false;
    if (boundIdRef.current === user.id && status === 'ready') return true;
    if (pendingRef.current) return pendingRef.current;
    const p = bind(user.id).finally(() => { pendingRef.current = null; });
    pendingRef.current = p;
    return p;
  }, [user?.id, status, bind]);

  return (
    <Ctx.Provider value={{ status, appUserId, ensureIdentity }}>
      {children}
    </Ctx.Provider>
  );
}

export function useRevenueCatIdentity(): RevenueCatIdentityContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRevenueCatIdentity must be used within RevenueCatIdentityProvider');
  return v;
}
